import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import "./CaseDetails.scss";
import { useEffect, useState } from "react";
import caseStore from "../../stores/CaseStore";
import {
  Case as CaseType,
  CaseStatus,
  ContactChoice,
} from "../../stores/CaseStore/types";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { City, cityMap, convertCityMapToArray } from "../../utils/cityCodes";
import authStore from "../../stores/AuthStore";
import { UserRoles } from "../../stores/AuthStore/types";
import { caseValidations } from "./validations";
import feedbackStore from "../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../stores/FeedbackStore/types";
import offerStore from "../../stores/OfferStore";
import OfferDialog from "../../components/Offer/OfferDialog";
import OfferResultDialog from "../../components/Offer/OfferResultDialog";
import { Offer } from "../../stores/OfferStore/types";
import configStore, { Flags } from "../../stores/ConfigStore";
import { UpdateCaseStatus } from "./UpdateCaseStatus";
import useIsMobile from "../../hooks/useIsMobile";
import ReactGA from "react-ga4";

const defaultValues = {
  id: "",
  status: CaseStatus.DEACTIVE,
  type: "",
  subType: "",
  caseProvince: "",
  userProvince: "",
  description: "",
  budget: "500-1000",
  currency: "TL",
  contactChoice: [],
  createDate: 0,
  updateDate: 0,
};

interface CaseProps {
  caseItem?: CaseType;
  reviewMode?: boolean;
  afterSubmit?: (updatedCase: CaseType, isSuccessful: boolean) => void;
  onCancel?: () => void;
}

const CaseDetails = observer((props: CaseProps) => {
  const isAdmin = authStore.userHasRole(UserRoles.ADMIN);
  const isMobile = useIsMobile();
  const { id } = props.caseItem || useParams<{ id: string | undefined }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [caseItem, setCaseItem] = useState<CaseType | undefined>(
    props.caseItem,
  );
  const [caseOffers, setCaseOffers] = useState<Offer[]>();
  const [activeStep, setActiveStep] = useState(0);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isCategoriesLoaded, setIsCategoriesLoaded] = useState(false);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [openOfferDialog, setOpenOfferDialog] = useState(false);
  const [openOfferResultDialog, setOpenOfferResultDialog] = useState(false);
  const [kvkkChecked, setKvkkChecked] = useState(isAdmin ? true : false);
  const [cityList, setCityList] = useState<City[]>([]);
  const allSubCategories = Object.values(
    caseStore.caseCategories?.subCategories || {},
  ).flat();
  const [tooltipMap, setTooltipMap] = useState<{ [key: string]: boolean }>({});

  // Form handlers
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    trigger,
  } = useForm<CaseType>({ defaultValues });

  const editMode =
    (authStore.userHasRole(UserRoles.USER) &&
      authStore.user.id === caseItem?.ownerId) ||
    id === "new" ||
    props.caseItem;

  const offer = offerStore.lawyerOffers.find((offer) => offer.caseId === id);

  if (
    !isAdmin &&
    (!authStore.user.emailValid ||
      !authStore.user.phone ||
      !authStore.user.firstName)
  ) {
    feedbackStore.setShowFeedback(true, {
      severity: FeedbackSeverity.WARNING,
      message: "update_contact_info",
    });
    navigate("/profile");
  }

  useEffect(() => {
    if (offerStore.userOffers.length === 0) {
      offerStore.fetchUserOffers();
    }
    if (
      authStore.userHasRole(UserRoles.LAWYER) &&
      offerStore.lawyerOffers.length > 0
    ) {
      offerStore.fetchLawyerOffers();
    }

    caseStore.fetchCaseCategories().then(() => {
      setIsCategoriesLoaded(true);
    });

    setCityList(convertCityMapToArray());
  }, []);

  useEffect(() => {
    if (id && id !== "new") {
      const existingCase = caseStore.userCases.find(
        (userCase) => `${userCase.id}` === `${id}`,
      );
      if (existingCase) {
        // @ts-ignore
        setCaseItem(existingCase);
        setActiveStep(5);
        setIsPageLoaded(true);
      } else {
        caseStore.fetchCase(id).then((userCase) => {
          setCaseItem(userCase);
          setActiveStep(5);
          setIsPageLoaded(true);
        });
      }
    } else {
      if (caseStore.presetCaseItem) {
        setCaseItem(() => {
          const preset = { ...caseStore.presetCaseItem } as CaseType;
          caseStore.presetCaseItem = null;
          return preset;
        });
      } else if (props.reviewMode) {
        setActiveStep(5);
      }

      setIsPageLoaded(true);
    }
  }, [caseStore.userCases]);

  useEffect(() => {
    if (caseItem && caseStore.caseCategories) {
      setValue("id", caseItem.id);
      setValue("type", caseItem.type);
      setValue("subType", caseItem.subType);
      setValue("caseProvince", caseItem.caseProvince);
      setValue("userProvince", caseItem.userProvince);
      setValue("description", caseItem.description);
      setValue("budget", caseItem.budget);
      setValue("currency", caseItem.currency);
      setValue("contactChoice", caseItem.contactChoice || []);
      setValue("createDate", caseItem.createDate);
      setValue("updateDate", caseItem.updateDate);
      trigger().then();
    } else {
      setValue("budget", configStore.appConfig.offerRanges[0]);
    }
  }, [caseItem, caseStore.caseCategories]);

  useEffect(() => {
    setCaseOffers(
      offerStore.userOffers.filter(
        (offer) => offer.caseId.toString() === id?.toString(),
      ),
    );
  }, [offerStore.userOffers]);

  useEffect(() => {
    if (activeStep === 1) {
      trigger("type").then(() => {
        if (subCategories?.length === 0) {
          setSubCategories(
            caseStore.caseCategories?.subCategories[getValues("type")] || [],
          );
        }

        trigger("subType").then();
      });
    }
  }, [activeStep]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = (data: CaseType) => {
    setHasSubmit(true);

    ReactGA.event({
      category: "User Interaction",
      action: "Submit Case Click",
      label: "User Case Submit",
      value: Number.parseInt(authStore.user.id),
    });

    data = {
      ...data,
      language: configStore.currentLanguage,
      ownerRole: UserRoles.USER,
      status: props.caseItem?.status || CaseStatus.PENDING,
    };

    (!!id && id !== "new"
      ? caseStore.updateCase(data, id)
      : caseStore.createCase(data)
    )
      .then(() => {
        if (isAdmin || props.reviewMode) {
          if (props.afterSubmit) {
            props.afterSubmit(data, true);
          }
        } else {
          caseStore.fetchUserCases();
          navigate("/cases");
        }

        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.SUCCESS,
          message: isAdmin || props.reviewMode ? "success" : "case-saved",
        });
      })
      .catch(() => {
        setHasSubmit(false);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "case-save-failed",
        });
      });
  };

  const handleOfferDialogOpen = () => {
    setOpenOfferDialog(true);
  };

  const handleTooltipOpen = (tooltipName: string, open: boolean) => {
    setTooltipMap((prev) => ({ ...prev, [tooltipName]: open }));
  };

  const summary = (
    <>
      <main>
        {authStore.userHasRole(UserRoles.ADMIN) && (
          <>
            <p>
              <b>{t("user-id")}: </b>
            </p>
            <p>
              <span>{caseItem?.ownerId}</span>
            </p>
          </>
        )}
        {authStore.userHasRole(UserRoles.LAWYER) && (
          <>
            <p>
              <b>{t("case-code")}: </b>
            </p>
            <p>
              <span>{caseItem?.id}</span>
            </p>
          </>
        )}
        <p>
          <b>{t("case-type")}: </b>
        </p>
        <p>
          <span>{t(getValues("type"))}</span>
        </p>
        <p>
          <b>{t("case-sub-type")}: </b>
        </p>
        <p>
          <span>{t(getValues("subType"))}</span>
        </p>
        <p>
          <b>{t("case-city")}: </b>
        </p>
        <p>
          <span>{cityMap[getValues("caseProvince")]}</span>
        </p>
        <p>
          <b>{t("user-city")}: </b>
        </p>
        <p>
          <span>{cityMap[getValues("userProvince") || ""]}</span>
        </p>
        <p>
          <b>{t("case-short-desc")}: </b>
        </p>
        <p>
          <span>{getValues("description")}</span>
        </p>
        <p>
          <b>{t("budget")}: </b>
        </p>
        <p>
          <span>{t(`${getValues("budget")}-budget`)}</span>
        </p>
        <p>
          <b>{t("contact-choice")}: </b>
        </p>
        <p>
          <span>
            {t(
              getValues("contactChoice")
                .map((choice) => t(choice))
                .join(","),
            )}
          </span>
        </p>
      </main>
    </>
  );

  const offers = (
    <div className="seeOffers">
      {id !== "new" && (
        <div>
          <Button
            variant={"outlined"}
            color={"info"}
            onClick={() => setOpenOfferResultDialog(true)}
          >
            {t("see-offers", [`${caseOffers?.length || 0}`])}
          </Button>
          {caseItem && caseOffers && (
            <OfferResultDialog
              offers={caseOffers}
              open={openOfferResultDialog}
              setOpen={setOpenOfferResultDialog}
            />
          )}
        </div>
      )}
    </div>
  );

  const header = (
    <>
      <h3>
        <span>{t("case-code")}: </span>
        <span>{caseItem?.id}</span>
      </h3>
      {caseItem && <UpdateCaseStatus caseItem={caseItem} />}
    </>
  );

  const fieldTooltip = (tooltipName: string, title: React.ReactNode) => (
    <Tooltip
      title={title}
      placement={isMobile ? "bottom-end" : "right"}
      open={tooltipMap[tooltipName] || false}
      tabIndex={1}
      onMouseEnter={() => handleTooltipOpen(tooltipName, true)}
      onMouseLeave={() => handleTooltipOpen(tooltipName, false)}
      onClick={() => handleTooltipOpen(tooltipName, !tooltipMap[tooltipName])}
      onBlur={() => handleTooltipOpen(tooltipName, false)}
    >
      <i className={`fa fa-info-circle tooltip-icon ${tooltipName}`} />
    </Tooltip>
  );

  const form = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb">
        * {t("your-case-limit", [`${configStore.appConfig.caseLimit}`])}
      </div>
      <Stepper
        orientation={"vertical"}
        activeStep={activeStep}
        sx={{ display: props.reviewMode ? "none" : "block" }}
      >
        <Step disabled={true}>
          <StepLabel onClick={() => setActiveStep(0)}>
            {t("case-city-selection")}
          </StepLabel>
          <StepContent className="pt">
            <Controller
              name={"userProvince"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <InputLabel id="user-city-label">
                    {t("please-select-user-city")}
                  </InputLabel>
                  <Select
                    displayEmpty
                    onChange={(e) => {
                      trigger("userProvince").then();
                      onChange(e);
                    }}
                    value={value}
                    label={t("please-select-user-city")}
                    labelId="user-city-label"
                  >
                    {cityList.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name={"caseProvince"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <InputLabel id="case-city-label">
                    {t("please-select-case-city")}
                  </InputLabel>
                  <Select
                    displayEmpty
                    onChange={(e) => {
                      trigger("caseProvince").then();
                      onChange(e);
                    }}
                    value={value}
                    label={t("please-select-case-city")}
                    labelId="case-city-label"
                  >
                    {cityList
                      .filter(({ code }) => code !== "0")
                      .map(({ code, name }) => (
                        <MenuItem key={code} value={code}>
                          {name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
            <footer>
              <Button
                hidden={
                  `${getValues("userProvince")}` === "" ||
                  `${getValues("caseProvince")}` === ""
                }
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("caseProvince")}` !== "" &&
              `${getValues("userProvince")}` !== "" &&
              setActiveStep(1)
            }
          >
            {t("case-category-selection")}
          </StepLabel>
          <StepContent className="pt">
            <FormControl className="mb">
              <Autocomplete
                options={allSubCategories}
                getOptionKey={(option) => option}
                getOptionLabel={(option) =>
                  `${t(option)} - ${t(option.split("-s-")[0])}`
                }
                noOptionsText={t("please-write-category")}
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (cat) =>
                      inputValue &&
                      t(cat).toLowerCase().includes(inputValue.toLowerCase()),
                  )
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    // Find the type (main category) for the selected subType
                    const foundType = Object.entries(
                      caseStore.caseCategories?.subCategories || {},
                    ).find(([type, subTypes]) =>
                      subTypes.includes(newValue),
                    )?.[0];

                    if (foundType) {
                      setValue("type", foundType);
                      setSubCategories(
                        caseStore.caseCategories.subCategories[foundType],
                      );
                    }
                    setValue("subType", newValue);
                    trigger("subType").then();
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="filled"
                    label={
                      <>
                        <i className="fa fa-search" />
                        &nbsp;&nbsp;
                        {t("search")}
                      </>
                    }
                  />
                )}
                isOptionEqualToValue={(option, val) => option === val}
              />

              <Divider className="mb mt" />
            </FormControl>
            <Controller
              name={"type"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <InputLabel id="main-category-label">
                    {t("please-select-main-category")}
                  </InputLabel>
                  <Select
                    displayEmpty
                    onChange={(e) => {
                      if (
                        caseStore.caseCategories?.subCategories !== undefined
                      ) {
                        setSubCategories(
                          caseStore.caseCategories.subCategories[
                            e.target.value
                          ],
                        );
                        setValue("subType", "");
                      }

                      onChange(e);
                    }}
                    value={value}
                    label={t("please-select-main-category")}
                    labelId="main-category-label"
                  >
                    {caseStore.caseCategories?.mainCategories.map(
                      (mainCategory) =>
                        mainCategory.length < 6 ? (
                          <ListSubheader key={mainCategory}>
                            {t(`${mainCategory}`)}
                          </ListSubheader>
                        ) : (
                          <MenuItem
                            key={`${mainCategory}`}
                            value={`${mainCategory}`}
                          >
                            {t(`${mainCategory}`)}
                          </MenuItem>
                        ),
                    )}
                  </Select>
                  {fieldTooltip("main-category", t("main-category-info"))}
                </FormControl>
              )}
            />
            <Controller
              name={"subType"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <InputLabel
                    id="sub-category-label"
                    hidden={getValues("type") === "" || !subCategories?.length}
                  >
                    {t("please-select-sub-category")}
                  </InputLabel>
                  <Select
                    displayEmpty
                    hidden={getValues("type") === "" || !subCategories?.length}
                    onChange={(e) => {
                      trigger("subType").then();
                      onChange(e);
                    }}
                    value={value}
                    label={t("please-select-sub-category")}
                    labelId="sub-category-label"
                  >
                    {subCategories?.map((subCategory) => (
                      <MenuItem key={`${subCategory}`} value={`${subCategory}`}>
                        {t(`${subCategory}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={
                  (subCategories?.length && `${getValues("subType")}` === "") ||
                  `${getValues("type")}` === ""
                }
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("type")}` !== "" &&
              `${getValues("subType")}` !== "" &&
              setActiveStep(2)
            }
          >
            {t("case-description")}
          </StepLabel>
          <StepContent>
            <Controller
              name={"description"}
              control={control}
              rules={{ ...caseValidations.description }}
              render={({ field: { onChange, value } }) => (
                <Box sx={{ position: "relative" }}>
                  <InputLabel className="mb mt">
                    {t("please-enter-short-desc")}
                  </InputLabel>
                  <div style={{ position: "relative" }}>
                    <TextField
                      error={!!errors.description}
                      placeholder={t("please-write-here")}
                      multiline={true}
                      rows={5}
                      onChange={(e) => {
                        onChange(e);
                        trigger("description").then();
                      }}
                      value={value}
                    />
                    {fieldTooltip(
                      "description",
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t("description-info"),
                        }}
                      />,
                    )}
                  </div>
                  <Stack
                    direction={"row"}
                    justifyContent={"flex-end"}
                    className={"mb"}
                  >
                    {`${value.length}/${caseValidations.description.maxLength}`}
                  </Stack>
                  <div className="error">
                    {errors.description &&
                      t(`short-desc-${errors.description.type}`)}
                  </div>
                </Box>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={!!errors.description}
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("description")}` !== "" && setActiveStep(3)
            }
          >
            {t("budget")}
          </StepLabel>
          <StepContent className="pt">
            <Controller
              name={"budget"}
              control={control}
              rules={{ ...caseValidations.budget }}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  {authStore.userHasRole(UserRoles.USER) ? (
                    <Select
                      fullWidth
                      className="mb mt"
                      value={value}
                      onChange={(e) => {
                        if (e.target.value) {
                          onChange(e);
                          trigger("budget").then();
                        }
                      }}
                    >
                      {configStore.appConfig?.offerRanges?.map((range) => (
                        <MenuItem key={range} value={range}>
                          {t(`${range}-budget`)}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <TextField
                      error={!!errors.budget}
                      placeholder={t("please-write-here")}
                      multiline={true}
                      rows={1}
                      onChange={(e) => {
                        if (e.target.value) {
                          onChange(e);
                          trigger("budget").then();
                        }
                      }}
                      value={value}
                    />
                  )}
                  <div className="error mt">
                    {errors.budget && t(`budget-${errors.budget.type}`)}
                  </div>
                  {fieldTooltip("budget", t("budget-info"))}
                </FormControl>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={`${getValues("budget")}` === ""}
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("description")}` !== "" && setActiveStep(4)
            }
          >
            {t("contact-choice")}
          </StepLabel>
          <StepContent>
            <Controller
              name={"contactChoice"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  {value.length < 1 && (
                    <InputLabel id="contact-choice-label">
                      {t("please-select-one-contact-choice")}
                    </InputLabel>
                  )}
                  <Select
                    fullWidth
                    multiple
                    labelId="contact-choice-label"
                    placeholder={"0"}
                    className="mt"
                    value={value}
                    renderValue={(selected) =>
                      selected.map((choice) => t(choice)).join(",")
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        onChange(e);
                        trigger("contactChoice").then();
                      }
                    }}
                  >
                    {Object.values(ContactChoice).map((item) => (
                      <MenuItem key={item} value={item}>
                        <Checkbox checked={value.includes(item)} />
                        <ListItemText primary={t(item)} />
                      </MenuItem>
                    ))}
                  </Select>
                  <div className="error mt">
                    {errors.budget && t(`contact-${errors.budget.type}`)}
                  </div>
                  {fieldTooltip("contact", t("contact-info"))}
                </FormControl>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={getValues("contactChoice").length === 0}
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              getValues("contactChoice").length !== 0 && setActiveStep(5)
            }
          >
            {t("summary")}
          </StepLabel>
          <StepContent className="mt">
            {!props.reviewMode && (
              <header>
                <h3>{t("please-review-case-details")}</h3>
              </header>
            )}
            <main>{summary}</main>
            {!isAdmin && (
              <section className="mt mb">
                <Checkbox
                  id="kvkk-text"
                  value={kvkkChecked}
                  checked={kvkkChecked}
                  onChange={() => setKvkkChecked((prevState) => !prevState)}
                />
                <label
                  htmlFor="kvkk-text"
                  dangerouslySetInnerHTML={{ __html: t("kvkk") }}
                />
              </section>
            )}
            <footer>
              <Button
                onClick={handleBack}
                disabled={hasSubmit}
                variant={"contained"}
                color={"inherit"}
              >
                {hasSubmit ? <CircularProgress size={"16px"} /> : t("back")}
              </Button>
              <Button
                type="submit"
                disabled={hasSubmit || !kvkkChecked}
                variant={"contained"}
              >
                {hasSubmit ? <CircularProgress size={"16px"} /> : t("submit")}
              </Button>
            </footer>
          </StepContent>
        </Step>
      </Stepper>
      {props.reviewMode && (
        <>
          {summary}
          <Controller
            name={"description"}
            control={control}
            rules={{ ...caseValidations.description }}
            render={({ field: { onChange, value } }) => (
              <>
                <InputLabel className="mt">
                  <h3>{t("change-short-desc")}</h3>
                </InputLabel>
                <TextField
                  fullWidth
                  error={!!errors.description}
                  placeholder={t("please-write-here")}
                  multiline={true}
                  rows={5}
                  onChange={(e) => {
                    onChange(e);
                    trigger("description").then();
                  }}
                  value={value}
                />
                <div className="error">
                  {errors.description &&
                    t(`short-desc-${errors.description.type}`)}
                </div>
              </>
            )}
          />
          <footer className={"mt"}>
            <Button type="submit" disabled={hasSubmit} variant={"contained"}>
              {hasSubmit ? <CircularProgress size={"16px"} /> : t("save")}
            </Button>
          </footer>
        </>
      )}
    </form>
  );

  const offerActions = (
    <div className="offerActions">
      {caseItem && (
        <OfferDialog
          caseItem={caseItem}
          open={openOfferDialog}
          setOpen={setOpenOfferDialog}
          offerId={offer?.id}
          readonly={!!offer}
        />
      )}
      {offerStore.lawyerOffers.length < configStore.appConfig.offerLimit ? (
        <Button
          onClick={handleOfferDialogOpen}
          variant={"contained"}
          size={"large"}
          color={"warning"}
        >
          {!!offer ? t("your-offer") : t("give-offer")}{" "}
          <i className="fal fa-comment-dollar"></i>
        </Button>
      ) : (
        (configStore.getFlag(Flags.isPremiumEnabled) ?? (
          <Button color={"warning"} size={"large"} variant={"outlined"}>
            {t("more-offers-with-premium", [
              `${offerStore.lawyerOffers.length}`,
              `${configStore.appConfig.offerLimit}`,
            ])}
          </Button>
        ))
      )}
    </div>
  );

  const content = editMode ? (
    <>
      {!isAdmin && !props.reviewMode && offers}
      {id !== "new" && `${id}` !== "" && header} {form}
    </>
  ) : (
    <>
      {summary} {offerActions}
    </>
  );

  if (id === undefined && props.caseItem === undefined) {
    return <div className="caseContainer">{t("missing-case-id-in-url")}</div>;
  } else {
    return (
      <div className={props.reviewMode ? "" : "caseContainer"}>
        {isPageLoaded && isCategoriesLoaded ? (
          content
        ) : (
          <div className="loadingContainer">
            <CircularProgress />
          </div>
        )}
      </div>
    );
  }
});

export default CaseDetails;
